import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { supabase, auth } from '../config/supabase';
import { getMetricsBySection } from '../config/metricsConfig';
import MetricItem from './metrics/MetricItem';
import PageHeader from './PageHeader';
import AccessBlocked from './AccessBlocked';
import { LOGIN_MESSAGE_KEY } from '../utils/userAccess';
import { useAutoSync } from '../hooks/useAutoSync';
import { useStatCardsBatch } from '../hooks/useStatCardsBatch';
import { StatCardsBatchContext } from '../context/StatCardsBatchContext';
import { getMetricPathFromEndpoint } from '../utils/metricPath';
import { type UserAccess } from '../utils/userAccess';
import {
  clearSuperadminViewStore,
  getSuperadminViewStore,
  resolveEffectiveDashboardAccess,
} from '../utils/superadminStoreView';
import { isSuperadmin } from '../utils/userRole';
import './Dashboard.css';

// Create a client for React Query
const queryClient = new QueryClient();

const DashboardContent = () => {
  const navigate = useNavigate();
  const [selectedStore, setSelectedStore] = useState<string | null>(null);
  const [isLoadingStore, setIsLoadingStore] = useState(true);
  const [userAccess, setUserAccess] = useState<UserAccess | null>(null);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: ''
  });

  // Hook para sincronización automática
  const { isSyncing, syncStatus, triggerManualSync } = useAutoSync({
    storeId: selectedStore,
    enabled: !!selectedStore && userAccess?.canAccessDashboard === true
  });

  // Validar acceso del usuario y tienda
  useEffect(() => {
    const loadAccess = async () => {
      setIsLoadingStore(true);
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();

        if (authError || !user) {
          setIsLoadingStore(false);
          return;
        }

        const access = await resolveEffectiveDashboardAccess(user);
        setUserAccess(access);

        if (isSuperadmin(access.role) && !getSuperadminViewStore()) {
          navigate('/admin', { replace: true });
          return;
        }

        if (!access.userActive) {
          sessionStorage.setItem(
            LOGIN_MESSAGE_KEY,
            'Tu cuenta está deshabilitada. Contactá al administrador de BorisMetrics.'
          );
          await auth.signOut();
          return;
        }

        if (access.canAccessDashboard && access.storeId) {
          setSelectedStore(access.storeId);
        } else {
          setSelectedStore(null);
        }
      } catch (error) {
        console.error('Error validando acceso:', error);
      } finally {
        setIsLoadingStore(false);
      }
    };

    loadAccess();
  }, [navigate]);

  // Obtener sección actual basada en la ruta
  const location = useLocation();
  const pathname = location.pathname;
  
  let currentSection = 'general';
  if (pathname.startsWith('/ventas')) {
    currentSection = 'ventas';
  } else if (pathname.startsWith('/cyd')) {
    currentSection = 'cyd';
  } else if (pathname.startsWith('/operaciones')) {
    currentSection = 'operaciones';
  } else if (pathname.startsWith('/clientes')) {
    currentSection = 'clientes';
  }

  const sectionTitle = useMemo(() => {
    const titles: Record<string, string> = {
      general: 'Dashboard general',
      ventas: 'Ventas',
      cyd: 'Cambios y devoluciones',
      operaciones: 'Operaciones',
      clientes: 'Clientes',
    };
    return titles[currentSection] ?? 'Dashboard general';
  }, [currentSection]);

  // Obtener métricas para la sección actual
  const sectionMetrics = useMemo(() => getMetricsBySection(currentSection), [currentSection]);

  const statCardPaths = useMemo(
    () =>
      sectionMetrics
        .filter((metric) => metric.type === 'stat-card' && metric.apiEndpoint)
        .map((metric) => getMetricPathFromEndpoint(metric.apiEndpoint))
        .filter((path) => path.length > 0),
    [sectionMetrics]
  );

  const {
    data: statCardsBatchData,
    isLoading: statCardsBatchLoading,
    isError: statCardsBatchError,
    error: statCardsBatchErrorObj,
  } = useStatCardsBatch({
    storeId: selectedStore,
    metricPaths: statCardPaths,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    enabled: !!selectedStore && !!dateRange.startDate && !!dateRange.endDate && statCardPaths.length > 0,
  });

  const statCardsBatchContextValue = useMemo(
    () => ({
      data: statCardsBatchData,
      isLoading: statCardsBatchLoading,
      isError: statCardsBatchError,
      error: statCardsBatchErrorObj,
    }),
    [statCardsBatchData, statCardsBatchLoading, statCardsBatchError, statCardsBatchErrorObj]
  );

  // Manejar cambio de rango de fechas
  const handleDateRangeChange = useCallback((startDate: string, endDate: string) => {
    console.log('📥 Dashboard - Recibiendo cambio de fechas:', { startDate, endDate });
    setDateRange({ startDate, endDate });
    console.log('📊 Dashboard - dateRange actualizado:', { startDate, endDate });
  }, []);

  // Generar estilos del grid simplificado
  const gridStyles = useMemo(() => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
  }), []);

  if (isLoadingStore) {
    return (
      <div className="dashboard">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <span>Cargando dashboard…</span>
        </div>
      </div>
    );
  }

  if (userAccess && !userAccess.canAccessDashboard && userAccess.blockReason) {
    const superadminView = isSuperadmin(userAccess.role) && getSuperadminViewStore();
    return (
      <div className="dashboard">
        <AccessBlocked
          reason={userAccess.blockReason}
          storeName={userAccess.storeName}
          onBackToAdmin={
            superadminView
              ? () => {
                  clearSuperadminViewStore();
                  navigate('/admin', { replace: true });
                }
              : undefined
          }
        />
      </div>
    );
  }

  return (
    <div className="dashboard">
      <PageHeader
        title={sectionTitle}
        showControls={!!selectedStore}
        onDateRangeChange={handleDateRangeChange}
        initialStartDate={dateRange.startDate}
        initialEndDate={dateRange.endDate}
        isSyncing={isSyncing}
        syncStatus={syncStatus}
        onRefresh={triggerManualSync}
      />

      <div className="dashboard-body">
      {/* Grid de métricas */}
      {selectedStore && dateRange.startDate && dateRange.endDate ? (
        <StatCardsBatchContext.Provider value={statCardsBatchContextValue}>
          <div className="dashboard-grid" style={gridStyles}>
            {sectionMetrics.map(metric => {
              const position = metric.positions[currentSection as keyof typeof metric.positions];
              if (!position) return null;

              console.log(`📊 Dashboard - Renderizando métrica ${metric.id} con fechas:`, {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                storeId: selectedStore
              });

              return (
                <div
                  key={metric.id}
                  style={{
                    gridColumn: `${position.x + 1} / span ${position.w}`,
                    gridRow: `${position.y + 1} / span ${position.h}`
                  }}
                >
                  <MetricItem
                    config={metric}
                    storeId={selectedStore}
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                  />
                </div>
              );
            })}
          </div>
        </StatCardsBatchContext.Provider>
      ) : (
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <span>Inicializando dashboard…</span>
        </div>
      )}
      </div>
    </div>
  );
};

const Dashboard = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <DashboardContent />
    </QueryClientProvider>
  );
};

export default Dashboard; 